<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import axios from 'axios';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    emailType: {
        type: String,
        required: true
    },
    clientLocale: {
        type: String,
        default: 'en'
    },
    // Hide the language selector when the recipient's language is decided
    // elsewhere (e.g. budget emails resolve it per recipient)
    showLocale: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['change']);

const LOCALES = ['en', 'pt', 'es'];
const localeOptions = LOCALES.map(l => ({ label: l.toUpperCase(), value: l }));

const expanded = ref(false);
const locale = ref(LOCALES.includes(props.clientLocale) ? props.clientLocale : 'en');
const message = ref('');
const defaults = ref({ en: '', pt: '', es: '' });
const loaded = ref(false);
const loadingDefaults = ref(false);

// Mirrors the server-side fallback (locale -> English), so the preview always
// matches the message that would actually be sent
const defaultFor = (l) => defaults.value[l] || defaults.value.en || '';
const currentDefault = computed(() => defaultFor(locale.value));

const loadDefaults = async () => {
    if (loaded.value || loadingDefaults.value) return;
    loadingDefaults.value = true;

    try {
        const url = proxy.route
            ? proxy.route('admin.email-settings.default-messages', props.emailType)
            : `/admin/email-settings/default-messages/${props.emailType}`;
        const { data } = await axios.get(url);
        defaults.value = { ...defaults.value, ...(data.default_messages?.client || {}) };
        loaded.value = true;

        if (!message.value.trim()) {
            message.value = currentDefault.value;
        }
    } catch (e) {
        // Panel still works as a free-text message; retried on next expand
    } finally {
        loadingDefaults.value = false;
    }
};

const toggle = () => {
    expanded.value = !expanded.value;
    if (expanded.value) {
        loadDefaults();
    }
};

// Switching language swaps an untouched text to the new language's default;
// a message already edited by the admin is kept as written.
watch(locale, (newLocale, oldLocale) => {
    if (message.value.trim() === defaultFor(oldLocale).trim()) {
        message.value = defaultFor(newLocale);
    }
});

const restoreDefault = () => {
    message.value = currentDefault.value;
};

const payload = computed(() => {
    const text = message.value.trim();

    return {
        message: text && text !== currentDefault.value.trim() ? text : null,
        locale: locale.value,
    };
});

watch(payload, (value) => emit('change', value), { immediate: true });
</script>

<template>
    <div class="border border-surface-200 rounded-lg">
        <button
            type="button"
            class="w-full flex items-center justify-between px-4 py-3 text-left"
            @click="toggle"
        >
            <span class="text-sm font-medium text-surface-700">
                <i class="pi pi-envelope mr-2 text-surface-500"></i>
                {{ $t('Customize email message') }}
            </span>
            <i :class="expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-surface-500 text-sm"></i>
        </button>

        <div v-show="expanded" class="px-4 pb-4 space-y-3">
            <div class="flex items-center justify-between gap-3">
                <div v-if="showLocale" class="flex items-center gap-2">
                    <span class="text-sm text-surface-600">{{ $t('Email language') }}:</span>
                    <SelectButton
                        v-model="locale"
                        :options="localeOptions"
                        optionLabel="label"
                        optionValue="value"
                        :allowEmpty="false"
                    />
                </div>
                <div v-else></div>
                <Button
                    :label="$t('Restore default')"
                    icon="pi pi-refresh"
                    text
                    size="small"
                    severity="secondary"
                    @click="restoreDefault"
                />
            </div>

            <Textarea
                v-model="message"
                rows="5"
                autoResize
                class="w-full"
                maxlength="5000"
                :placeholder="$t('Optional message shown highlighted in the email')"
            />

            <p class="text-xs text-surface-500">
                {{ $t('Leave empty (or unchanged) to use the default message configured in Email Settings.') }}
            </p>
        </div>
    </div>
</template>
